import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Custom Home Building',
    description: 'Personalized residential construction tailored to your vision.'
  },
  {
    title: 'Commercial Construction',
    description: 'Expert construction of office, retail, and industrial spaces.'
  },
  {
    title: 'Project Management',
    description: 'Comprehensive oversight from planning to completion.'
  },
  {
    title: 'Permit Handling',
    description: 'Complete management of all necessary permits and approvals.'
  },
  {
    title: 'Quality Assurance',
    description: 'Rigorous quality control throughout construction.'
  },
  {
    title: 'Sustainable Building',
    description: 'Green building practices and energy-efficient solutions.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1541976590-713941681591?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale New Construction'
  },
  {
    url: 'https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Commercial Building'
  },
  {
    url: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Custom Home'
  }
];

export function NewConstructionPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>New Construction Services | Licensed in NY & CT</title>
        <meta name="description" content="Expert new construction services in Westchester County, NY and Fairfield County, CT. Licensed and insured builder specializing in residential and commercial construction." />
        <meta name="keywords" content="new construction Westchester, new construction Fairfield, commercial builder NY CT, custom home builder" />
        <link rel="canonical" href="https://renovationwestchester.com/services/new-construction" />
      </Helmet>

      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                New Construction Services
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Licensed and insured in both Westchester County, NY and Fairfield County, CT. 
                Our comprehensive new construction services cover both residential and commercial 
                projects, with a focus on quality and excellence.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County, NY & Fairfield County, CT</span>
              </div>
              <Link 
                to="/consultation"
                className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="New Construction in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Our Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Start Your Project?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Let's discuss your construction project. Our expert team is ready to bring 
              your vision to life with quality craftsmanship and professional service.
            </p>
            <Link 
              to="/consultation"
              className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}